/**
 * Skins (docs/PARITY-STEP-1-PLAN.md section 5): the desktop's six built-in skins, read from
 * its own files (src/main/resources/styles/skins), and skins imported from the desktop's
 * `.css` files. A skin is only a set of the desktop's colour tokens (`SkinToken`); each
 * value must be a plain hex colour, and nothing else in the file is used, so a skin can't
 * bring in rules, fonts, URLs or anything else. Colours are applied as the app's `--lf-*`
 * custom properties, through the CSSOM, never as style text.
 *
 * The web holds text to WCAG AA (4.5:1), which some desktop skins don't reach in a browser
 * (Forge's accent, for one): `readable` darkens or lightens just those colours until they do.
 */

/** `SkinToken`'s CSS names, as the web's custom properties use them (without `-lf-`). The web has no use for `base`. */
export const SKIN_TOKENS = [
  'canvas', 'surface', 'border', 'control', 'base', 'chrome', 'on-chrome', 'ink', 'ink-body', 'ink-muted',
  'accent', 'accent-hover', 'on-accent', 'chip', 'danger', 'document', 'document-ink',
] as const;
export type SkinToken = (typeof SKIN_TOKENS)[number];

export interface Skin {
  readonly id: string;
  readonly name: string;
  readonly description: string;
  readonly author: string;
  readonly builtIn: boolean;
  readonly colors: Readonly<Partial<Record<SkinToken, string>>>;
}

const HEADER_COMMENT = /^\s*\/\*([\s\S]*?)\*\//;
const HEADER_FIELD = /^[\s*]*@(name|description|author)[ \t]+(.+?)\s*$/gm;
const ROOT_BLOCK = /(?<![\w.#-])\.root\s*\{([^}]*)\}/g;
const TOKEN_DECLARATION = /(-lf-[a-z0-9-]+)\s*:\s*([^;{}]+?)\s*;/g;
const HEX = /^#(?:[0-9a-fA-F]{3}|[0-9a-fA-F]{6})$/;

/** `SkinCss.readHeader` and the `.root` tokens, as the desktop reads a skin file; anything that isn't a known token with a hex colour is dropped. */
export function parseSkin(css: string, id: string, builtIn = false): Skin {
  const header: Record<string, string> = {};
  const comment = HEADER_COMMENT.exec(css);
  if (comment) {
    for (const [, field, value] of comment[1]!.matchAll(HEADER_FIELD)) {
      const key = field!.toLowerCase();
      if (!(key in header)) header[key] = value!.slice(0, 200);
    }
  }
  const colors: Partial<Record<SkinToken, string>> = {};
  for (const [, declarations] of css.matchAll(ROOT_BLOCK)) {
    for (const [, name, value] of declarations!.matchAll(TOKEN_DECLARATION)) {
      const token = name!.slice('-lf-'.length) as SkinToken;
      if ((SKIN_TOKENS as readonly string[]).includes(token) && HEX.test(value!)) colors[token] = expandHex(value!);
    }
  }
  return { id, name: header['name'] ?? id, description: header['description'] ?? '', author: header['author'] ?? '', builtIn, colors };
}

function expandHex(hex: string): string {
  const h = hex.slice(1).toLowerCase();
  return `#${h.length === 3 ? [...h].map((c) => c + c).join('') : h}`;
}

// ---- contrast ----

function channels(hex: string): [number, number, number] {
  const h = expandHex(hex).slice(1);
  return [0, 2, 4].map((i) => parseInt(h.slice(i, i + 2), 16)) as [number, number, number];
}

function luminance(hex: string): number {
  const [r, g, b] = channels(hex).map((c) => {
    const s = c / 255;
    return s <= 0.03928 ? s / 12.92 : ((s + 0.055) / 1.055) ** 2.4;
  }) as [number, number, number];
  return 0.2126 * r + 0.7152 * g + 0.0722 * b;
}

/** WCAG's contrast ratio of two colours. */
export function contrast(a: string, b: string): number {
  const [hi, lo] = [luminance(a), luminance(b)].sort((x, y) => y - x) as [number, number];
  return (hi + 0.05) / (lo + 0.05);
}

function mix(hex: string, toward: string, amount: number): string {
  const [a, b] = [channels(hex), channels(toward)];
  return `#${a.map((c, i) => Math.round(c + (b[i]! - c) * amount).toString(16).padStart(2, '0')).join('')}`;
}

/** `fg`, moved toward black on light backgrounds or white on dark ones, just far enough to reach `ratio` against every one of `backgrounds`. */
export function readable(fg: string, backgrounds: readonly string[], ratio = 4.5): string {
  const dark = backgrounds.reduce((sum, bg) => sum + luminance(bg), 0) / backgrounds.length < 0.18;
  const toward = dark ? '#ffffff' : '#000000';
  for (let step = 0; step <= 20; step++) {
    const candidate = step === 0 ? fg : mix(fg, toward, step / 20);
    if (backgrounds.every((bg) => contrast(candidate, bg) >= ratio)) return candidate;
  }
  return toward;
}

export type Scheme = 'light' | 'dark';

/** The colours to apply, with every text colour readable on what it's drawn on, and whether the skin is light or dark. */
export function appliedColors(skin: Skin): { scheme: Scheme; colors: Partial<Record<SkinToken, string>> } {
  const c = { ...skin.colors };
  const canvas = c.canvas ?? '#f5f2ee';
  const pages = [canvas, c.surface ?? canvas, c.control ?? canvas];
  for (const token of ['ink', 'ink-body', 'ink-muted', 'accent', 'danger'] as const) {
    if (c[token]) c[token] = readable(c[token]!, pages);
  }
  if (c.accent) {
    // Buttons: the accent behind text in on-accent, whichever of it, white or black reads best.
    const options = [c['on-accent'] ?? '#ffffff', '#ffffff', '#000000'];
    c['on-accent'] = options.reduce((best, o) => (contrast(o, c.accent!) > contrast(best, c.accent!) ? o : best));
    if (c['accent-hover']) c['accent-hover'] = readable(c['accent-hover'], [c['on-accent']]);
  }
  if (c['on-chrome'] && c.chrome) c['on-chrome'] = readable(c['on-chrome'], [c.chrome]);
  if (c['document-ink'] && c.document) c['document-ink'] = readable(c['document-ink'], [c.document]);
  return { scheme: luminance(canvas) < 0.18 ? 'dark' : 'light', colors: c };
}

// ---- the built-in skins, from the desktop's own files ----

const BUILT_IN_FILES = import.meta.glob('../../../src/main/resources/styles/skins/*.css', { query: '?raw', import: 'default', eager: true }) as Record<string, string>;

/** `SkinCatalog.BUILT_IN_ORDER`. */
const BUILT_IN_ORDER = ['forge', 'ember-night', 'parchment', 'frostpeak', 'verdant', 'high-contrast'];

export const BUILT_IN_SKINS: readonly Skin[] = BUILT_IN_ORDER.flatMap((id) => {
  const file = Object.entries(BUILT_IN_FILES).find(([path]) => path.endsWith(`/${id}.css`));
  return file ? [parseSkin(file[1], id, true)] : [];
});

/** The web's own default: Forge, or Ember Night when the device is in dark mode (styles.css). */
export const AUTOMATIC = 'automatic';

/** Applies a skin to the page, or the automatic one for `null`. */
export function applySkin(skin: Skin | null, root: HTMLElement = document.documentElement): void {
  for (const token of SKIN_TOKENS) root.style.removeProperty(`--lf-${token}`);
  if (!skin) {
    delete root.dataset['scheme'];
    return;
  }
  const { scheme, colors } = appliedColors(skin);
  root.dataset['scheme'] = scheme;
  for (const [token, value] of Object.entries(colors)) root.style.setProperty(`--lf-${token}`, value);
}
